<template>
    <section class="rtb-list">
        <header class="rtb-list__header">
            <div>
                <h1 class="rtb-list__title">{{ heading.title }}</h1>
                <p class="rtb-list__subtitle">{{ heading.subtitle }}</p>
            </div>
            <a v-if="createUrl && !statsMode" :href="createUrl" class="rtb-list__create">
                <span aria-hidden="true">+</span> Создать кампанию
            </a>
        </header>

        <RtbCampaignsFilters
            v-if="hasLoaded && (totalCount > 0 || hasActiveFilters)"
            v-model:search="filters.search"
            v-model:status="filters.status"
            v-model:advertiserId="filters.advertiserId"
            v-model:sort="filters.sort"
            :statuses="statuses"
            :total-count="totalCount"
            :advertisers="advertisers"
            :has-active-filters="hasActiveFilters"
            @reset="resetFilters"
        />

        <div v-if="!hasLoaded && isLoading" class="rtb-list__state" aria-busy="true">
            <span class="rtb-list__spinner" aria-hidden="true"></span>
            Загружаем кампании…
        </div>

        <div v-else-if="hasLoadError && !rows.length" class="rtb-list__state rtb-list__state--error">
            <p>Не удалось загрузить кампании.</p>
            <button type="button" class="rtb-list__button" @click="load()">Повторить</button>
        </div>

        <div v-else-if="hasLoaded && totalCount === 0 && !hasActiveFilters" class="rtb-list__state">
            <p class="rtb-list__state-title">Кампаний пока нет</p>
            <p>{{ emptyText }}</p>
            <a v-if="createUrl && !statsMode" :href="createUrl" class="rtb-list__create">Создать первую кампанию</a>
        </div>

        <div v-else-if="hasLoaded && !rows.length" class="rtb-list__state">
            <p class="rtb-list__state-title">Ничего не найдено</p>
            <p>Измените условия поиска или сбросьте фильтры.</p>
            <button type="button" class="rtb-list__button" @click="resetFilters">Сбросить фильтры</button>
        </div>

        <div v-else-if="hasLoaded" :class="['rtb-list__table-wrap', { 'is-loading': isLoading }]" :aria-busy="isLoading">
            <table class="rtb-list__table">
                <thead>
                    <tr>
                        <th>Кампания</th>
                        <th>Статус</th>
                        <th>Период</th>
                        <th>Бюджет</th>
                        <th>Показы</th>
                        <th><span class="visually-hidden">Действия</span></th>
                    </tr>
                </thead>
                <tbody>
                    <RtbCampaignRow
                        v-for="campaign in rows"
                        :key="campaign.id"
                        :campaign="campaign"
                        :is-pending="isRowPending(campaign)"
                        :stats-mode="statsMode"
                        @action="action => runAction(campaign, action)"
                    />
                </tbody>
            </table>
        </div>

        <footer v-if="hasLoaded && rows.length" class="rtb-list__pagination">
            <span class="rtb-list__range">{{ rangeLabel }}</span>
            <div class="rtb-list__pages">
                <label class="rtb-list__per-page">
                    По
                    <select v-model.number="filters.perPage" class="rtb-list__select">
                        <option v-for="size in RTB_LIST_PER_PAGE_OPTIONS" :key="size" :value="size">{{ size }}</option>
                    </select>
                </label>
                <template v-if="meta.last_page > 1">
                    <button type="button" class="rtb-list__button" :disabled="isLoading || meta.current_page <= 1" @click="filters.page = meta.current_page - 1">
                        ← Назад
                    </button>
                    <span class="rtb-list__page">{{ meta.current_page }} из {{ meta.last_page }}</span>
                    <button type="button" class="rtb-list__button" :disabled="isLoading || meta.current_page >= meta.last_page" @click="filters.page = meta.current_page + 1">
                        Вперёд →
                    </button>
                </template>
            </div>
        </footer>
    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted } from 'vue';
import RtbCampaignRow from './RtbCampaignRow.vue';
import RtbCampaignsFilters from './RtbCampaignsFilters.vue';
import { useRtbCampaignList } from '../../../../composables/useRtbCampaignList';
import { RTB_CAMPAIGN_TYPE, RTB_LIST_PER_PAGE_OPTIONS, RTB_ROW_ACTION } from '../../../../constants/rtbCampaigns';
import { formatInteger } from '../../../../utils/numberFormat';
import { PLURAL, pluralize } from '../../../../utils/plural';

const props = defineProps({
    type: { type: String, required: true, validator: value => Object.values(RTB_CAMPAIGN_TYPE).includes(value) },
    advertisers: { type: Array, default: () => [] },
    createUrl: { type: String, default: null },
    statsMode: { type: Boolean, default: false },
});

const {
    filters,
    rows,
    meta,
    statuses,
    totalCount,
    isLoading,
    hasLoaded,
    hasLoadError,
    hasActiveFilters,
    load,
    resetFilters,
    isRowPending,
    toggleStatus,
    deleteCampaign,
    exportScreens,
    dispose,
} = useRtbCampaignList(props.type);

const heading = computed(() => {
    if (props.statsMode) {
        return { title: 'Статистика кампаний по пакетам', subtitle: 'Выберите кампанию, чтобы открыть детальную статистику.' };
    }
    if (props.type === RTB_CAMPAIGN_TYPE.PACKAGE) {
        return { title: 'Кампании по пакетам', subtitle: 'RTB-кампании на готовых пакетах экранов.' };
    }
    if (props.type === RTB_CAMPAIGN_TYPE.POI) {
        return { title: 'POI-кампании', subtitle: 'Ставки и креативы как у RTB, экраны берутся из POI-слоя.' };
    }
    return { title: 'RTB-кампании', subtitle: 'Кампании с ручным выбором экранов и ставок.' };
});

const emptyText = computed(() => {
    if (props.type === RTB_CAMPAIGN_TYPE.PACKAGE) {
        return 'Создайте кампанию: выберите пакет экранов, период и креативы — бюджет и ставки рассчитаются автоматически.';
    }
    if (props.type === RTB_CAMPAIGN_TYPE.POI) {
        return 'Создайте кампанию по POI-слою: экраны подставятся из слоя, ставки задаются как в RTB.';
    }
    return 'Создайте кампанию: выберите креативы, экраны и задайте бюджет.';
});

const rangeLabel = computed(() => {
    const { current_page: page, per_page: perPage, total } = meta.value;
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, total);
    return `${formatInteger(from)}–${formatInteger(to)} из ${formatInteger(total)} ${pluralize(total, PLURAL.CAMPAIGN)}`;
});

const runAction = (campaign, action) => {
    switch (action) {
        case RTB_ROW_ACTION.EDIT: window.location.assign(campaign.urls.edit); break;
        case RTB_ROW_ACTION.STATISTICS: window.location.assign(campaign.urls.statistics); break;
        case RTB_ROW_ACTION.TOGGLE: toggleStatus(campaign); break;
        case RTB_ROW_ACTION.EXPORT_SCREENS: exportScreens(campaign); break;
        case RTB_ROW_ACTION.DELETE: deleteCampaign(campaign); break;
        default: break;
    }
};

onMounted(load);
onBeforeUnmount(dispose);
</script>

<style scoped lang="scss">
@use '../../../../../scss/pages/admin/admin-tokens' as *;

.rtb-list {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
    max-width: 1400px;
    margin: 0 auto;
    padding: $spacing-xl;
}

.rtb-list__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: $spacing-lg;
}

.rtb-list__title {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: $admin-text-primary;
}

.rtb-list__subtitle {
    margin: $spacing-xs 0 0;
    font-size: $font-size-base;
    color: $admin-text-muted;
}

.rtb-list__create {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    padding: 0.55rem 1.1rem;
    border-radius: $radius-md;
    background: $admin-primary-color;
    font-size: $font-size-base;
    font-weight: 600;
    color: #fff;
    text-decoration: none;
    white-space: nowrap;

    &:hover { background: $admin-primary-hover; color: #fff; }
}

.rtb-list__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-sm;
    padding: 3.5rem $spacing-xl;
    border: 1px dashed $admin-border-hover;
    border-radius: $radius-xl;
    background: $admin-bg-primary;
    text-align: center;
    color: $admin-text-muted;

    p { margin: 0; }

    &--error { border-color: #fecaca; background: $admin-danger-bg; color: $admin-danger-color; }
}

.rtb-list__state-title {
    font-size: $font-size-xl;
    font-weight: 600;
    color: $admin-text-primary;
}

.rtb-list__spinner {
    width: 28px;
    height: 28px;
    border: 3px solid $admin-border-color;
    border-top-color: $admin-primary-color;
    border-radius: 50%;
    animation: rtb-list-spin 0.8s linear infinite;
}

@keyframes rtb-list-spin {
    to { transform: rotate(360deg); }
}

.rtb-list__table-wrap {
    border: 1px solid $admin-border-color;
    border-radius: $radius-xl;
    background: $admin-bg-primary;
    overflow-x: auto;
    transition: opacity 0.15s;

    &.is-loading { opacity: 0.55; pointer-events: none; }
}

.rtb-list__table {
    width: 100%;
    border-collapse: collapse;

    th {
        padding: $spacing-md $spacing-lg;
        border-bottom: 1px solid $admin-border-color;
        background: $admin-bg-secondary;
        font-size: $font-size-sm;
        font-weight: 600;
        text-align: left;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        color: $admin-text-muted;
        white-space: nowrap;
    }

    :deep(tbody tr:last-child td) { border-bottom: none; }
}

.rtb-list__pagination {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-md;
    font-size: $font-size-sm;
    color: $admin-text-muted;
}

.rtb-list__pages {
    display: flex;
    align-items: center;
    gap: $spacing-md;
}

.rtb-list__per-page {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    margin: 0;
}

.rtb-list__select {
    height: 32px;
    padding: 0 $spacing-lg 0 $spacing-sm;
    border: 1px solid $admin-border-hover;
    border-radius: $radius-md;
    background: $admin-bg-primary;
    font-size: $font-size-sm;
}

.rtb-list__page {
    color: $admin-text-secondary;
}

.rtb-list__button {
    padding: 0.4rem 0.9rem;
    border: 1px solid $admin-border-hover;
    border-radius: $radius-md;
    background: $admin-bg-primary;
    font-size: $font-size-sm;
    font-weight: 500;
    color: $admin-text-secondary;
    cursor: pointer;

    &:hover:not(:disabled) { background: $admin-bg-hover; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
}

@media (max-width: $breakpoint-md) {
    .rtb-list { padding: $spacing-lg; }
    .rtb-list__title { font-size: $font-size-3xl; }
}
</style>
